import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Map, { Source, Layer, Marker } from "react-map-gl";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { Button, Col, Collapse, Row, Tooltip } from "antd";
import { LuMap } from "react-icons/lu";
import polylabel from "polylabel";
import CheapRuler from "cheap-ruler";
import * as turf from "@turf/turf";
import bbox from "@turf/bbox";
import { ModuleContext } from "../../../context/Context";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import "mapbox-gl/dist/mapbox-gl.css";
import "../style.css";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedCounty,
  setSelectedFips,
  setSelectedState,
} from "../../../Redux/filterSlice";
import { fetchGeoCountiesByState } from "../../../Redux/geo/geoThunk";
import { fetchAllPracticeDataByCounty } from "../../../Redux/practice/practiceThunk";

function FieldMap({
  setMapZoomLevel,
  setMapViewBounds,
  userLocation,
  pinCenter,
  setPinCenter,
  fieldBoundary,
  selectedField,
  setSelectedField,
  drawingMode,
  editingMode,
  pinningMode,
  onMapReady,
  forceZoomToCounty,
  setForceZoomToCounty,
}) {
  const mapRef = useRef(null); // stores the Mapbox instance
  const viewRef = useRef(null); // still use this if you want to control react-map-gl component (optional)
  const preventNextZoom = useRef(false);

  const [baseLayer, setBaseLayer] = useState(
    "mapbox://styles/mapbox/light-v11"
  );
  const [layerSelectOpen, setLayerSelectOpen] = useState(false);
  const [safeLayerOn, setSafeLayerOn] = useState(false);
  const [crepLayerOn, setCrepLayerOn] = useState(false);
  const [floodplainLayerOn, setFloodplainLayerOn] = useState(false);
  const [waterLayerOn, setWaterLayerOn] = useState(false);
  const [cursor, setCursor] = useState("auto");
  const [interactiveLayerIds, setInteractiveLayerIds] = useState([
    "county-boundary",
  ]);
  const [fieldHighlightFilter, setFieldHighlightFilter] = useState([
    "in",
    "gid",
    "",
  ]);
  const [fieldSelectFilter, setFieldSelectFilter] = useState(["in", "gid", ""]);
  const [countySelectFilter, setCountySelectFilter] = useState([
    "in",
    "FIPS",
    "",
  ]);
  const [countyHighlightFilter, setCountyHighlightFilter] = useState([
    "in",
    "FIPS",
    "",
  ]);
  const [measurementData, setMeasurementData] = useState({
    type: "FeatureCollection",
    features: [],
  });
  const { featureList, updateFeatureList, practicePreference } =
    useContext(ModuleContext);
  const stateFilter = [
    "match",
    ["get", "STATE"],
    ["AR", "IA", "IL", "IN", "LA", "MO", "MS", "OH"],
    true,
    false,
  ];
  const { selectedFips, selectedCounty, selectedState } = useSelector(
    (state) => state.filter
  );
  const dispatch = useDispatch();
  const [mapLoaded, setMapLoaded] = useState(false);
  const safeLegends = [
    {
      key: "1",
      label: "Illinois",
      children: (
        <p>
          <span>
            <div className="legend-IL_PH"></div>
            <div className="legend-label">Illinois Pheasant Initiative</div>
          </span>
          <br />
          <span>
            <div className="legend-IL_QI"></div>
            <div className="legend-label">Illinois Quail Initiative</div>
          </span>
        </p>
      ),
    },
    {
      key: "2",
      label: "Indiana",
      children: (
        <p>
          <span>
            <div className="legend-IN_IB"></div>
            <div className="legend-label">Indiana Bat</div>
          </span>
          <br />
          <span>
            <div className="legend-IN_HS"></div>
            <div className="legend-label">Indiana Henslows Sparrow</div>
          </span>
          <br />
          <span>
            <div className="legend-IN_NB"></div>
            <div className="legend-label">Indiana Northern Bobwhite Quail</div>
          </span>
          <br />
          <span>
            <div className="legend-IN_RP"></div>
            <div className="legend-label">Indiana Ring-necked Pheasant</div>
          </span>
        </p>
      ),
    },
    {
      key: "3",
      label: "Iowa",
      children: (
        <p>
          <span>
            <div className="legend-IA_ES"></div>
            <div className="legend-label">
              Iowa Early Successional-Neotropical Birds
            </div>
          </span>
          <br />
          <span>
            <div className="legend-IA_GG"></div>
            <div className="legend-label">Iowa Gaining Ground</div>
          </span>
          <br />
          <span>
            <div className="legend-IA_GR"></div>
            <div className="legend-label">Iowa Grand River</div>
          </span>
          <br />
          <span>
            <div className="legend-IA_PR"></div>
            <div className="legend-label">Iowa Pheasant Recovery</div>
          </span>
          <br />
          <span>
            <div className="legend-IA_QH"></div>
            <div className="legend-label">Iowa Quail Habitat</div>
          </span>
        </p>
      ),
    },
    {
      key: "4",
      label: "Mississippi",
      children: (
        <p>
          <span>
            <div className="legend-MS_BB"></div>
            <div className="legend-label">Mississippi Black Bear</div>
          </span>
          <br />
          <span>
            <div className="legend-MS_BP"></div>
            <div className="legend-label">Mississippi Black Belt Prairie</div>
          </span>
          <br />
          <span>
            <div className="legend-MS_BQ"></div>
            <div className="legend-label">Mississippi Bobwhite Quail</div>
          </span>
        </p>
      ),
    },
    {
      key: "5",
      label: "Missouri",
      children: (
        <p>
          <span>
            <div className="legend-MO_QM"></div>
            <div className="legend-label">Missouri Bobwhite Quail</div>
          </span>
          <br />
          <span>
            <div className="legend-MO_SE"></div>
            <div className="legend-label">Missouri Sand Ecosystem</div>
          </span>
        </p>
      ),
    },
    {
      key: "6",
      label: "Ohio",
      children: (
        <p>
          <span>
            <div className="legend-OH_MH"></div>
            <div className="legend-label">
              Ohio Pollinator and Monarch Habitat
            </div>
          </span>
          <br />
          <span>
            <div className="legend-OH_UH"></div>
            <div className="legend-label">Ohio Upland Bird Habitat</div>
          </span>
        </p>
      ),
    },
  ];

  const customDrawStyles = [
    {
      id: "gl-draw-line-inactive",
      type: "line",
      filter: ["all", ["==", "active", "false"], ["==", "$type", "LineString"]],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": "#3b9ddd",
        "line-width": 2,
        "line-dasharray": ["literal", [2, 4]],
      },
    },
    {
      id: "gl-draw-line-active",
      type: "line",
      filter: ["all", ["==", "active", "true"], ["==", "$type", "LineString"]],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": "#3b9ddd",
        "line-width": 2,
        "line-dasharray": ["literal", [2, 4]],
      },
    },
    {
      id: "gl-draw-polygon-fill",
      type: "fill",
      filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
      paint: {
        "fill-color": "#3b9ddd",
        "fill-outline-color": "#3b9ddd",
        "fill-opacity": 0.3,
      },
    },
    {
      id: "gl-draw-polygon-stroke-inactive",
      type: "line",
      filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": "#3b9ddd",
        "line-width": 2,
        "line-dasharray": ["literal", [2, 4]],
      },
    },
    {
      id: "gl-draw-polygon-and-line-vertex",
      type: "circle",
      filter: ["all", ["==", "meta", "vertex"], ["!=", "mode", "static"]],
      paint: {
        "circle-radius": 5,
        "circle-color": "#fff",
        "circle-stroke-width": 2,
        "circle-stroke-color": "#3b9ddd",
      },
    },
  ];

  const drawControl = new MapboxDraw({
    defaultMode: "draw_polygon",
    controls: {
      polygon: "true",
      trash: "true",
    },
    styles: customDrawStyles,
  });

  const handleLayerSelect = () => {
    setLayerSelectOpen(!layerSelectOpen);
  };

  const onClick = (e) => {
    if (
      interactiveLayerIds.includes("county-boundary") &&
      e.features.length &&
      !editingMode &&
      !drawingMode
    ) {
      const feature = e.features[0];
      const fips = feature.properties.FIPS;
      const stateCode = feature.properties.STATE;
      const countyName = feature.properties.COUNTYNAME;

      dispatch(setSelectedFips(String(fips).padStart(5, "0")));

      dispatch(setSelectedState(stateCode));
      dispatch(fetchGeoCountiesByState(stateCode));
      dispatch(setSelectedCounty(countyName));
      dispatch(fetchAllPracticeDataByCounty(fips));
    }

    if (
      interactiveLayerIds.includes("field-boundary") &&
      e.features.length &&
      !editingMode
    ) {
      const feature = e.features[0];
      setSelectedField(feature);
      setFieldSelectFilter(["in", "gid", feature.id]);
    }

    if (pinningMode) {
      setPinCenter({ lng: e.lngLat.lng, lat: e.lngLat.lat });
    }
  };

  const onMouseEnter = () => {
    setCursor("pointer");
  };

  const onMouseLeave = () => {
    if (drawingMode || pinningMode) {
      setCursor("crosshair");
    } else {
      setCursor("auto");
    }
    setCountyHighlightFilter(["in", "FIPS", ""]);
    setFieldHighlightFilter(["in", "gid", ""]);
  };

  const onMouseMove = (e) => {
    if (
      interactiveLayerIds.includes("county-boundary") &&
      e.features.length &&
      !editingMode
    ) {
      const feature = e.features[0];
      setCountyHighlightFilter(["in", "FIPS", feature.properties.FIPS]);
    }
    if (
      interactiveLayerIds.includes("field-boundary") &&
      e.features.length &&
      !editingMode
    ) {
      const feature = e.features[0];
      setFieldHighlightFilter(["in", "gid", feature.id]);
    }
  };

  const onMove = (e) => {
    if (mapRef.current) {
      setMapViewBounds(mapRef.current.getBounds());
    }
    setCursor("grab");
  };

  const onMoveEnd = (e) => {
    if (drawingMode || pinningMode) {
      setCursor("crosshair");
    } else {
      setCursor("auto");
    }
  };

  const onZoom = (e) => {
    setMapZoomLevel(e.viewState.zoom);
  };

  const onUpdate = useCallback((e) => {
    const newList = [e.features[0]];
    updateFeatureList(newList);
  }, []);

  const onDelete = useCallback((e) => {
    const newList = featureList.filter(
      (feature) => feature.id !== e.features[0].id
    );
    updateFeatureList(newList);
  }, []);

  const zoomToCounty = useCallback(
    (force = false) => {
      const map = mapRef.current;
      const normalizedFips = String(selectedFips).padStart(5, "0");
      const isValidFips = /^\d{5}$/.test(normalizedFips);
      const zoomLevel = map.getZoom();
      if (!force && zoomLevel >= 8.5) {
        console.log(
          "Skipping zoomToCounty because map is already zoomed in:",
          zoomLevel
        );
        return;
      }
      if (!map || !isValidFips) {
        console.warn("⚠️ Not zooming due to invalid state (map or FIPS)");
        return;
      }

      const doZoom = () => {
        try {
          const features = map.querySourceFeatures("map-source", {
            sourceLayer: "c_08mr23-1pp4eg",
            filter: ["==", "FIPS", normalizedFips],
          });

          if (features.length) {
            const [minLng, minLat, maxLng, maxLat] = bbox(features[0]);
            map.fitBounds(
              [
                [minLng, minLat],
                [maxLng, maxLat],
              ],
              { padding: 40, duration: 1000 }
            );
            if (baseLayer !== "mapbox://styles/mapbox/satellite-streets-v12") {
              setBaseLayer("mapbox://styles/mapbox/satellite-streets-v12");
            }
          } else {
            console.warn(
              "🟡 County not in viewport. Zooming out to try again..."
            );
            map.fitBounds(
              [
                [-95.6, 30.3],
                [-89.1, 41.3],
              ],
              { padding: 40, duration: 800 }
            );
            setTimeout(doZoom, 1200); // Try again after zooming out
          }
        } catch (err) {
          console.error("❌ Failed to query source features:", err);
        }
      };

      if (map.isStyleLoaded()) {
        doZoom();
      } else {
        map.once("load", () => {
          doZoom();
        });
      }
    },
    [selectedFips, setBaseLayer]
  );

  const onRender = (e) => {
    var labelFeatures = [];
    var all = drawControl.getAll();
    var ruler = new CheapRuler(mapRef.current.getCenter().lat, "meters");
    if (all && all.features) {
      all.features.forEach(function (feature) {
        switch (turf.getType(feature)) {
          case "Point":
            // label Points
            if (feature.geometry.coordinates.length > 0) {
              var label =
                feature.geometry.coordinates[1].toFixed(4) +
                ", " +
                feature.geometry.coordinates[0].toFixed(4);
              labelFeatures.push(
                turf.point(feature.geometry.coordinates, {
                  type: "point",
                  label: label,
                })
              );
            }
            break;
          case "LineString":
            // label Lines
            if (feature.geometry.coordinates.length > 0) {
              var length = ruler.lineDistance(feature.geometry.coordinates);
              var midpoint = ruler.along(
                feature.geometry.coordinates,
                length / 2
              );
              var label = Math.round(length) + " m";
              labelFeatures.push(
                turf.point(midpoint, { type: "line", label: label })
              );
            }
            break;
          case "Polygon":
            // label Polygons
            if (
              feature.geometry.coordinates.length > 0 &&
              feature.geometry.coordinates[0].length > 3
            ) {
              var area = turf.area(feature);
              var label = Math.round(area * 0.000247105 * 100) / 100 + " acre";
              labelFeatures.push(
                turf.centroid(feature, {
                  properties: { type: "area", label: label },
                })
              );
            }
            break;
        }
      });
    }

    if (mapRef.current.getSource("measurements-source")) {
      mapRef.current.getSource("measurements-source").setData({
        type: "FeatureCollection",
        features: labelFeatures,
      });
    }
  };

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    const onLoad = () => {
      if (!mapLoaded) {
        console.log("Mapbox style loaded");
        setMapLoaded(true);
        if (onMapReady) onMapReady();
      }
    };

    // prevent duplicate listeners
    map.off("load", onLoad);
    map.on("load", onLoad);

    return () => {
      map.off("load", onLoad);
    };
  }, [mapLoaded, onMapReady]);

  useEffect(() => {
    if (mapRef.current && userLocation.longitude && userLocation.latitude) {
      mapRef.current.fitBounds(
        [
          [userLocation.longitude - 0.005, userLocation.latitude - 0.005],
          [userLocation.longitude + 0.005, userLocation.latitude + 0.005],
        ],
        { padding: 40, duration: 1000 }
      );
      setBaseLayer("mapbox://styles/mapbox/satellite-streets-v12");
    }
  }, [userLocation]);

  useEffect(() => {
    const normalizedFips = String(selectedFips).padStart(5, "0");
    const isValidFips = /^\d{5}$/.test(normalizedFips);
    const map = mapRef.current;

    if (
      selectedState &&
      selectedCounty &&
      selectedFips &&
      isValidFips &&
      mapLoaded &&
      lastZoomedFips.current !== normalizedFips
    ) {
      const currentZoom = map?.getZoom?.();

      if (currentZoom >= 8.5) {
        console.log("Skipping zoom: already zoomed in at", currentZoom);
        lastZoomedFips.current = normalizedFips;
        preventNextZoom.current = false; // reset flag
        return;
      }

      if (preventNextZoom.current) {
        console.log("Skipping zoom due to preventNextZoom flag.");
        preventNextZoom.current = false; // reset for next time
        lastZoomedFips.current = normalizedFips;
        return;
      }

      console.log("Zooming to selected county:", normalizedFips);
      zoomToCounty();
      lastZoomedFips.current = normalizedFips;
    }
  }, [selectedState, selectedCounty, selectedFips, mapLoaded, zoomToCounty]);

  useEffect(() => {
    const normalizedFips = String(selectedFips).padStart(5, "0");
    const isValidFips = /^\d{5}$/.test(normalizedFips);

    if (
      forceZoomToCounty &&
      selectedState &&
      selectedCounty &&
      isValidFips &&
      mapLoaded
    ) {
      console.log("Forcing zoom due to dropdown selection");
      zoomToCounty(true);
      setForceZoomToCounty(false);
      lastZoomedFips.current = normalizedFips;
    }
  }, [
    forceZoomToCounty,
    selectedFips,
    selectedState,
    selectedCounty,
    mapLoaded,
  ]);

  const lastZoomedFips = useRef(null);

  useEffect(() => {
    const normalizedFips = String(selectedFips).padStart(5, "0");
    const isValidFips = /^\d{5}$/.test(normalizedFips);

    if (
      selectedState &&
      selectedCounty &&
      selectedFips &&
      isValidFips &&
      mapLoaded && // wait for map load
      lastZoomedFips.current !== normalizedFips
    ) {
      zoomToCounty();
      lastZoomedFips.current = normalizedFips;
    }
  }, [selectedState, selectedCounty, selectedFips, mapLoaded, zoomToCounty]);

  useEffect(() => {
    if (mapRef.current && fieldBoundary) {
      setInteractiveLayerIds(["field-boundary"]);
      const [minLng, minLat, maxLng, maxLat] = bbox(fieldBoundary);
      mapRef.current.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { padding: 40, duration: 1000 }
      );
    }
  }, [fieldBoundary]);

  useEffect(() => {
    if (drawingMode) {
      setCursor("crosshair");
      if (mapRef.current) {
        mapRef.current.on("draw.create", onUpdate);
        mapRef.current.on("draw.update", onUpdate);
        mapRef.current.on("draw.delete", onDelete);
        mapRef.current.on("draw.render", onRender);
        mapRef.current.addControl(drawControl);
      }
    } else {
      setCursor("auto");
      if (mapRef.current) {
        mapRef.current.off("draw.create", onUpdate);
        mapRef.current.off("draw.update", onUpdate);
        mapRef.current.off("draw.delete", onDelete);
        mapRef.current.off("draw.render", onRender);
        if (mapRef.current.hasControl(drawControl)) {
          mapRef.current.removeControl(drawControl);
        }
      }
    }
  }, [drawingMode]);

  useEffect(() => {
    if (pinningMode) {
      setInteractiveLayerIds([]);
      setCursor("crosshair");
    } else {
      setCursor("auto");
    }
  }, [pinningMode]);

  useEffect(() => {
    if (mapRef.current && editingMode && selectedField) {
      const editControl = new MapboxDraw();
      mapRef.current.addControl(editControl);
      editControl.add(selectedField);
      editControl.changeMode("direct_select", { featureId: selectedField.id });
      mapRef.current.on("draw.update", (e) => {
        setSelectedField(e.features[0]);
      });
    }
  }, [editingMode]);

  return (
    <Map
      ref={viewRef}
      onLoad={(event) => {
        mapRef.current = event.target;
        setMapLoaded(true);
        if (onMapReady) onMapReady();
      }}
      mapboxAccessToken="pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
      initialViewState={{
        longitude: -91,
        latitude: 36,
        zoom: 4.5,
      }}
      style={{ width: "calc(100vw - 400px)", height: "83vh" }}
      mapStyle={baseLayer}
      cursor={cursor}
      interactiveLayerIds={interactiveLayerIds}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
      onMove={onMove}
      onMoveEnd={onMoveEnd}
      onZoom={onZoom}
    >
      <Tooltip placement="bottom" title="Layer Tool">
        <Button className="layer-select" onClick={handleLayerSelect}>
          <LuMap strokeWidth={3} />
        </Button>
      </Tooltip>
      {layerSelectOpen && (
        <div className="layer-options">
          <Col className="base-layers">
            <p>
              <b>Base Layers</b>
            </p>
            <p
              class={
                baseLayer === "mapbox://styles/mapbox/light-v11"
                  ? "layer-container-selected"
                  : "layer-container"
              }
            >
              <Button
                className="layer-button"
                onClick={() => setBaseLayer("mapbox://styles/mapbox/light-v11")}
              >
                <img
                  className="layer-thumbnail"
                  src="https://api.mapbox.com/styles/v1/mapbox/light-v11/static/-88.81,33.45,7.0,0,0/50x50?access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                />
                <label className="layer-label">Light Base Layer</label>
              </Button>
            </p>
            <p
              class={
                baseLayer === "mapbox://styles/mapbox/dark-v11"
                  ? "layer-container-selected"
                  : "layer-container"
              }
            >
              <Button
                className="layer-button"
                onClick={() => setBaseLayer("mapbox://styles/mapbox/dark-v11")}
              >
                <img
                  className="layer-thumbnail"
                  src="https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/-88.81,33.45,7.0,0,0/50x50?access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                />
                <label className="layer-label">Dark Base Layer</label>
              </Button>
            </p>
            <p
              class={
                baseLayer === "mapbox://styles/mapbox/satellite-streets-v12"
                  ? "layer-container-selected"
                  : "layer-container"
              }
            >
              <Button
                className="layer-button"
                onClick={() =>
                  setBaseLayer("mapbox://styles/mapbox/satellite-streets-v12")
                }
              >
                <img
                  className="layer-thumbnail"
                  src="https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/-88.81,33.45,7.0,0,0/50x50?access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                />
                <label className="layer-label">Satellite Base Layer</label>
              </Button>
            </p>
          </Col>
          <hr />
          <Col className="supporting-layers">
            <p>
              <b>Supporting Layers</b>
            </p>
            <p>
              <input
                type="checkbox"
                checked={safeLayerOn}
                disabled={mapRef.current.getZoom() < 6 ? true : false}
                onClick={(e) => {
                  if (e.currentTarget.checked) {
                    setSafeLayerOn(true);
                  } else {
                    setSafeLayerOn(false);
                  }
                }}
              />
              SAFE Boundaries
            </p>
            <p>
              <input
                type="checkbox"
                checked={crepLayerOn}
                disabled={mapRef.current.getZoom() < 5 ? true : false}
                onClick={(e) => {
                  if (e.currentTarget.checked) {
                    setCrepLayerOn(true);
                  } else {
                    setCrepLayerOn(false);
                  }
                }}
              />
              CREP Boundaries
            </p>
            {/* <p>
              <input
                type="checkbox"
                checked={floodplainLayerOn}
                onClick={(e) => {
                  if (e.currentTarget.checked) {
                    setFloodplainLayerOn(true);
                  } else {
                    setFloodplainLayerOn(false);
                  }
                }}
              />
              Floodplains
            </p>
            <p>
              <input
                type="checkbox"
                checked={waterLayerOn}
                onClick={(e) => {
                  if (e.currentTarget.checked) {
                    setWaterLayerOn(true);
                  } else {
                    setWaterLayerOn(false);
                  }
                }}
              />
              Water Features
            </p> */}
          </Col>
        </div>
      )}
      <div className="map-legend">
        <Col span={24}>
          <Row>
            <div className="legend-counties"></div>
            <b className="legend-label">Available Counties</b>
          </Row>
          {safeLayerOn && (
            <>
              <hr />
              <Row>
                <b className="legend-title">SAFE Boundaries</b>
                <Collapse items={safeLegends} style={{ width: "100%" }} />
              </Row>
            </>
          )}
          {crepLayerOn && (
            <>
              <hr />
              <Row>
                <b className="legend-title">CREP Boundaries</b>
                <p>
                  <span>
                    <div className="legend-DML"></div>
                    <div className="legend-label">North Central Iowa CREP</div>
                  </span>
                  <br />
                  <span>
                    <div className="legend-ILC"></div>
                    <div className="legend-label">Illinois CREP</div>
                  </span>
                  <br />
                  <span>
                    <div className="legend-INC"></div>
                    <div className="legend-label">Indiana CREP</div>
                  </span>
                  <br />
                  <span>
                    <div className="legend-MD"></div>
                    <div className="legend-label">Mississippi Delta CREP</div>
                  </span>
                  <br />
                  <span>
                    <div className="legend-LEW"></div>
                    <div className="legend-label">Lake Erie CREP</div>
                  </span>
                  <br />
                  <span>
                    <div className="legend-SRW"></div>
                    <div className="legend-label">Scioto River CREP</div>
                  </span>
                </p>
              </Row>
            </>
          )}
          {floodplainLayerOn && (
            <>
              <hr />
              <Row>
                <div className="legend-floodplain"></div>
                <b className="legend-label">Floodplains</b>
              </Row>
            </>
          )}
          {waterLayerOn && (
            <>
              <hr />
              <Row>
                <div className="legend-water"></div>
                <b className="legend-label">Water Features</b>
              </Row>
            </>
          )}
        </Col>
      </div>
      {drawingMode && (
        <Source
          id="measurements-source"
          type="geojson"
          data={{
            type: "FeatureCollection",
            features: [],
          }}
        >
          <Layer
            id="measurements"
            type="symbol"
            paint={{
              "text-color": "white",
            }}
            layout={{
              "text-field": "{label}",
              "text-size": 14,
            }}
          />
        </Source>
      )}
      {!fieldBoundary && (
        <>
          <Source
            id="map-source"
            type="vector"
            url="mapbox://chuck0520.5pq9omti"
            maxzoom={22}
            minzoom={0}
          >
            <Layer
              id="county-boundary"
              source-layer="c_08mr23-1pp4eg"
              type="fill"
              paint={{
                "fill-outline-color": "#800000",
                "fill-color": "transparent",
              }}
              minzoom={0}
              maxzoom={22}
              filter={stateFilter}
            />
            {baseLayer !== "mapbox://styles/mapbox/satellite-streets-v12" && (
              <Layer
                id="county-highlight"
                source-layer="c_08mr23-1pp4eg"
                type="line"
                paint={{
                  "line-color": "#800000",
                  "line-width": 3,
                  "line-opacity": 1,
                }}
                minzoom={0}
                maxzoom={22}
                filter={countyHighlightFilter}
              />
            )}
            {baseLayer !== "mapbox://styles/mapbox/satellite-streets-v12" && (
              <Layer
                id="county-select"
                source-layer="c_08mr23-1pp4eg"
                type="fill"
                paint={{
                  "fill-outline-color": "#800000",
                  "fill-color": "cyan",
                  "fill-opacity": 0.5,
                }}
                minzoom={0}
                maxzoom={22}
                filter={countySelectFilter}
              />
            )}
            {baseLayer !== "mapbox://styles/mapbox/satellite-streets-v12" && (
              <Layer
                id="county-labels"
                type="symbol"
                source="map-source"
                source-layer="c_08mr23-1pp4eg"
                layout={{
                  "text-field": ["get", "COUNTYNAME"],
                  "text-size": {
                    stops: [
                      [0, 0],
                      [4, 0],
                      [6, 10],
                      [8, 15],
                    ],
                  },
                }}
                paint={{ "text-color": "#800000" }}
                filter={stateFilter}
              />
            )}
          </Source>
          {pinCenter && (
            <Marker
              longitude={pinCenter.lng}
              latitude={pinCenter.lat}
              color="maroon"
            />
          )}
          {safeLayerOn && (
            <Source
              id="safe-source"
              type="vector"
              url="mapbox://chuck0520.21tzdrin"
              maxzoom={22}
              minzoom={0}
            >
              <Layer
                id="safe-layer"
                source-layer="safe_eight_states-d18wxz"
                type="fill"
                paint={{
                  "fill-outline-color": "black",
                  "fill-color": [
                    "match",
                    ["get", "CODE"],
                    "IL_PH",
                    "red",
                    "IL_QI",
                    "orange",
                    "IN_IB",
                    "purple",
                    "IN_HS",
                    "hotpink",
                    "IN_NB",
                    "orange",
                    "IN_RP",
                    "red",
                    "IA_ES",
                    "magenta",
                    "IA_GG",
                    "chocolate",
                    "IA_GR",
                    "navy",
                    "IA_PR",
                    "red",
                    "IA_QH",
                    "orange",
                    "MO_QM",
                    "orange",
                    "MO_SE",
                    "yellow",
                    "MS_BB",
                    "brown",
                    "MS_BP",
                    "gray",
                    "MS_BQ",
                    "orange",
                    "OH_MH",
                    "olive",
                    "OH_UH",
                    "crimson",
                    "transparent",
                  ],
                  "fill-opacity": 0.3,
                }}
              />
            </Source>
          )}
          {crepLayerOn && (
            <Source
              id="crep-source"
              type="vector"
              url="mapbox://chuck0520.8s78dmfv"
              maxzoom={22}
              minzoom={0}
            >
              <Layer
                id="crep-layer"
                source-layer="crep_a_states_gcs-1kvyce"
                type="fill"
                paint={{
                  "fill-outline-color": "black",
                  "fill-color": [
                    "match",
                    ["get", "CODE"],
                    "DML",
                    "red",
                    "ILC",
                    "orange",
                    "INC",
                    "purple",
                    "MD",
                    "green",
                    "LEW",
                    "navy",
                    "SRW",
                    "brown",
                    "transparent",
                  ],
                  "fill-opacity": 0.3,
                }}
              />
            </Source>
          )}
        </>
      )}
      {!!fieldBoundary && (
        <>
          <Source id="field-source" type="geojson" data={fieldBoundary}>
            <Layer
              id="field-boundary"
              type="fill"
              paint={{
                "fill-outline-color": "white",
                "fill-color": "white",
                "fill-opacity": 0.5,
              }}
            />
            <Layer
              id="field-boundary-highlight"
              type="line"
              paint={{
                "line-color": "cyan",
                "line-width": 2,
              }}
              filter={fieldHighlightFilter}
            />
            <Layer
              id="field-boundary-selected"
              type="fill"
              paint={{
                "fill-outline-color": "cyan",
                "fill-color": "cyan",
                "fill-opacity": 0.5,
              }}
              filter={fieldSelectFilter}
            />
          </Source>
          {safeLayerOn && (
            <Source
              id="safe-source"
              source-layer="SAFE_A_STATES_GCS-9clxpi"
              type="vector"
              url="mapbox://chuck0520.4ex8miv2"
              maxzoom={22}
              minzoom={0}
            >
              <Layer
                id="safe-layer"
                type="fill"
                paint={{
                  "fill-outline-color": "black",
                  "fill-color": [
                    "match",
                    ["get", "CODE"],
                    "IL_PH",
                    "red",
                    "IL_QI",
                    "orange",
                    "MO_QM",
                    "green",
                    "MO_SE",
                    "yellow",
                    "MS_BB",
                    "brown",
                    "MS_BP",
                    "gray",
                    "MS_BQ",
                    "purple",
                    "transparent",
                  ],
                  "fill-opacity": 0.3,
                }}
              />
            </Source>
          )}
        </>
      )}
    </Map>
  );
}
export default FieldMap;
